/* ============================================================================
   17 · Account — wearfigs.com "My Account", one screen per feature
   Figma 9Dhvc9N1v5FWO9b54mpCkN · page 1:14 "Account / MyFIGS" ·
   components board 2032:8274. One script for all eight pages:
     index.html       Profile dashboard (1873:138577 · 1969:146119)
     orders.html      Order History (1969:146521 · 1969:146570)
     order.html       Order Details, fulfilled (1975:149944 · 1975:149991)
     buy-again.html   Buy It Again + Quick Buy (1975:151819 · 1975:151841)
     favorites.html   Favorites + Sort (1975:152826 · 1975:153002)
     details.html     Account Details + Edit Account / Edit Password
     addresses.html   Addresses + Add / Edit Address
     preferences.html Preferences + Shopping Preference
   Every screen is static markup with the Figma's own content; this file only
   adds interaction and motion. A module a page doesn't carry simply doesn't
   start.
   ----------------------------------------------------------------------------
   What's being decided here:

     • Whether My Account can live inside the redesigned site rather than in
       a utility shell: the shared masthead, bag and footer, a quiet left
       column (a scrolling category row on phones, like the PLP filters), and
       the same Roboto Mono headings and FIGSv2 small-caps links as the PDP.
     • Whether account housekeeping can share the shopping templates' motion:
       the first screen opens with the Homepage/PLP/PDP intro (nav, heading,
       first block rise 16px on --mo-slow; plates fade and settle from 1.04),
       every block below the fold reveals on scroll in reading order, and
       product plates push in on hover.
     • Edits as side drawers, not pages: Edit Account Details, Edit Password,
       Add / Edit Address and Shopping Preference slide in from the right on
       the PDP drawers' timing, over the same Glass Overlay, at both widths.
     • Buy It Again as the account's one shopping moment: a card's + opens
       the PDP's Quick Buy with the size bought before already chosen, and
       ADD TO BAG puts the line in the lab's shared bag (the one spring on
       these pages, with the favourite heart).

   Reduced motion is a design state: reveals keep only their opacity, plates
   don't settle or push in, drawers crossfade instead of travelling.
   ========================================================================== */

import { mountFigsNav, addToFigsBag, openFigsBag } from '/src/figs-nav/figs-nav.js';
import { getFigsSizePrefs, setFigsSizePrefs } from '/src/figs-nav/figs-account.js';
import {
  animate, inView, stagger, hover, press, MO, state, tween, play, track, teardown,
  setSpeed, clearTracked, byPosition, SPRING_POP, $, $$, mqDesktop,
} from '/experiments/10-pdp-prototype/mo.js';
import { swapText, swapButtonLabel, pulse, shake, announce, bindRoving } from '/experiments/10-pdp-prototype/ui.js';
import { createDrawer } from '/experiments/10-pdp-prototype/drawers.js';
import { SIZE_NAMES } from '/experiments/10-pdp-prototype/data.js';

/* ============================================================
   HEADER + FOOTER — the shared nav and site footer, mounted once
   (outside boot() so Replay never mounts them twice). Its bag is the
   lab's one bag: whatever was added on another prototype is in it.
   ============================================================ */
mountFigsNav({
  promo: $('[data-fnav-promo]'),
  masthead: $('[data-fnav-masthead]'),
  footer: $('[data-fnav-footer]'),
  theme: 'light',
  homeHref: '/experiments/08-d-homepage/',
});

const page = document.body.dataset.page;

/* ============================================================
   1 · ACCOUNT NAV — on phones the category row scrolls; the current
   page is brought into view on load (with MANAGE, when it's the
   first of the Manage links).
   ============================================================ */
function initTabs() {
  const nav = $('[data-acct-nav]');
  const cur = nav && $('.acct-nav__link--current', nav);
  if (!cur) return;
  const sync = () => {
    if (mqDesktop.matches) return;
    const group = cur.closest('.acct-nav__group');
    const first = group && $('.acct-nav__link', group) === cur;
    const lead = (first && $('.acct-nav__label', group)) || cur;
    const box = nav.getBoundingClientRect();
    const l = lead.getBoundingClientRect().left;
    const r = cur.getBoundingClientRect().right;
    const pad = 16;
    if (r > box.right - pad || l < box.left + pad) nav.scrollLeft += l - box.left - pad;
  };
  sync();
  mqDesktop.addEventListener('change', sync);
}

/* Links with no prototype behind them stay "#" (lab convention) and go
   nowhere — no jump to the top. */
function initPlaceholders() {
  $$('.acct-page a[href="#"], .acct-drawer a[href="#"]').forEach((a) => {
    a.addEventListener('click', (e) => e.preventDefault());
  });
}

/* ============================================================
   2 · PRODUCT CARDS — mobile grid plates swipe through the product's
   photos with the Figma progress points; the heart toggles (spring
   pop on save — the affirmative moment the tokens reserve springs
   for). Pages: dashboard rows, Buy It Again, Favorites.
   ============================================================ */
function initCards() {
  $$('.acct-grid [data-card-track]').forEach((trackEl) => {
    const dots = $$('.acct-card__dot', trackEl.closest('.acct-card__media'));
    let raf = 0;
    trackEl.addEventListener('scroll', () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const i = Math.round(trackEl.scrollLeft / Math.max(1, trackEl.clientWidth));
        dots.forEach((d, k) => d.classList.toggle('acct-card__dot--active', k === i));
      });
    }, { passive: true });
  });

  $$('[data-fav]').forEach((btn) => {
    const ring = $('[data-fav-pulse]', btn);
    const name = btn.getAttribute('aria-label').replace(/^Favorite: /, '');
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      const saved = btn.getAttribute('aria-pressed') !== 'true';
      btn.setAttribute('aria-pressed', String(saved));
      if (saved) {
        play(btn, { scale: [0.8, 1] }, SPRING_POP);
        if (!state.reduced) track(animate(ring, { scale: [0.8, 1.8], opacity: [0.45, 0] }, { duration: 0.6, ease: MO.eStandard }));
        announce(`${name} added to Favorites`);
      } else {
        announce(`${name} removed from Favorites`);
      }
    });
  });
}

/* ============================================================
   3 · QUICK BUY — the PDP's Quick Buy (877:184737 · 1100:87561),
   filled from the card that opened it: its name, price, photos and
   colour. The size bought before (the drawer's data-qb-preselect) is
   already chosen; ADD TO BAG without one shakes the grid and shows the
   form's error instead of failing silently. A successful add confirms on
   the button (the licensed spring), the drawer closes itself and the
   shared bag opens on the new line.
   ============================================================ */
function initQuickBuy() {
  const root = $('[data-drawer="quickbuy"]');
  if (!root) return null;
  const drawer = createDrawer(root, { side: true });
  const gallery = $('[data-qb-gallery]', root);
  const thumb = $('[data-qb-thumb]', root);
  const title = $('[data-qb-title]', root);
  const colorName = $('[data-qb-color-name]', root);
  const swatches = $$('.acct-swatch', root);
  const sizeGrid = $('[data-qb-sizes]', root);
  const sizeLabel = $('[data-qb-size-label]', root);
  const error = $('[data-qb-error]', root);
  const typeGroups = $$('[data-qb-types]', root);
  const view = $('[data-qb-view]', root);
  const atb = $('[data-qb-atb]', root);
  const atbLabel = $('[data-qb-atb-label]', root);
  const preset = root.dataset.qbPreselect || '';

  let product = null;
  const sel = { color: '', size: '', type: 'Regular' };
  let busy = false;
  let closeTimer = 0;

  const check = (els, on) => els.forEach((el) => {
    const yes = on(el);
    el.setAttribute('aria-checked', String(yes));
    el.tabIndex = yes ? 0 : -1;
  });
  const atbText = () => `Add to Bag—$${product.price % 1 ? product.price.toFixed(2) : product.price}`;
  const sizeTiles = () => $$('.acct-tile--size', sizeGrid);
  const typeGroup = () => typeGroups.find((g) => !g.hidden);

  function updateProgress() {
    const max = gallery.scrollWidth - gallery.clientWidth;
    const frac = gallery.scrollWidth ? Math.min(1, gallery.clientWidth / gallery.scrollWidth) : 1;
    const w = Math.max(0.12, frac) * 100;
    const p = max > 0 ? gallery.scrollLeft / max : 0;
    thumb.style.width = `${w}%`;
    thumb.style.transform = `translateX(${((p * (100 - w)) / w) * 100}%)`;
  }
  let raf = 0;
  gallery.addEventListener('scroll', () => {
    if (!raf) raf = requestAnimationFrame(() => { raf = 0; updateProgress(); });
  }, { passive: true });

  /* colour: Core and Limited Edition are two radiogroups sharing one pick */
  $$('.acct-qb__swatches', root).forEach((group) => bindRoving(group, (sw) => {
    if (sw.dataset.color === sel.color) return;
    sel.color = sw.dataset.color;
    check(swatches, (s) => s === sw);
    swapText(colorName, sel.color);
  }));

  bindRoving(sizeGrid, (tile) => {
    sel.size = tile.dataset.size;
    check(sizeTiles(), (t) => t === tile);
    play(tile, { scale: [MO.scalePress, 1] }, { duration: MO.base, ease: MO.eEntrance });
    swapText(sizeLabel, `Size: ${tile.dataset.sizeName}`);
    if (!error.hidden) {
      play(error, { opacity: [1, 0] }, { duration: MO.quick, ease: MO.eExit })?.finished.then(() => { error.hidden = true; });
      sizeGrid.removeAttribute('data-invalid');
    }
  }, { selectOnArrow: false });

  typeGroups.forEach((group) => bindRoving($('[data-qb-type-grid]', group), (tile) => {
    if (tile.dataset.type === sel.type) return;
    sel.type = tile.dataset.type;
    check($$('.acct-tile--type', group), (t) => t === tile);
    swapText($('[data-qb-type-label]', group), `${group.dataset.typeWord}: ${sel.type}`);
  }));

  atb.addEventListener('click', async () => {
    if (busy || !product) return;
    if (!sel.size) {
      error.hidden = false;
      sizeGrid.setAttribute('data-invalid', '');
      announce('Please select a size.');
      sizeGrid.scrollIntoView({ block: 'center', behavior: state.reduced ? 'auto' : 'smooth' });
      setTimeout(() => {
        play(error, { opacity: [0, 1], y: [-4, 0] }, { duration: MO.base, ease: MO.eEntrance });
        shake(sizeGrid);
        sizeTiles()[0]?.focus({ preventScroll: true });
      }, state.reduced ? 0 : 280);
      return;
    }
    busy = true;
    addToFigsBag([{
      id: product.id, name: product.name, color: sel.color, size: sel.size, type: sel.type,
      price: product.price, image: product.images[0], url: product.url,
    }], { open: false });
    pulse(atb);
    announce(`${product.name}, ${sel.color}, ${sel.size} added to bag.`);
    await swapButtonLabel(atbLabel, 'Added to Bag', { affirm: true });
    closeTimer = setTimeout(() => {
      drawer.close().then(() => { busy = false; atbLabel.textContent = atbText(); openFigsBag(); });
    }, state.reduced ? 600 : 1100);
  });
  root.addEventListener('drawer:close', () => clearTimeout(closeTimer));

  /* the card is the source: its own markup carries everything Quick Buy shows */
  function fill(card) {
    const name = $('.acct-card__product', card);
    product = {
      id: card.dataset.id,
      name: name.textContent.trim(),
      price: Number($('.acct-card__price', card).textContent.replace(/[^0-9.]/g, '')),
      url: $('.acct-card__info', card).href,
      images: $$('.acct-card__img', card).map((img) => img.src),
    };
    title.innerHTML = name.innerHTML;
    gallery.innerHTML = '';
    product.images.forEach((src, i) => {
      const fig = document.createElement('figure');
      fig.className = 'acct-qb__slide';
      const img = document.createElement('img');
      img.src = src;
      img.alt = `${product.name}, image ${i + 1} of ${product.images.length}`;
      img.decoding = 'async';
      fig.append(img);
      gallery.append(fig);
    });
    gallery.scrollLeft = 0;

    sel.color = $('.acct-card__color', card).textContent.trim();
    colorName.textContent = sel.color;
    check(swatches, (s) => s.dataset.color === sel.color);

    sel.size = sizeTiles().some((t) => t.dataset.size === preset) ? preset : '';
    check(sizeTiles(), (t) => t.dataset.size === sel.size);
    if (!sel.size) sizeTiles()[0].tabIndex = 0;
    const tile = sizeTiles().find((t) => t.dataset.size === sel.size);
    sizeLabel.textContent = tile ? `Size: ${tile.dataset.sizeName}` : 'Select Size';
    sizeGrid.removeAttribute('data-invalid');
    error.hidden = true;

    const kind = card.dataset.kind === 'pant' ? 'pant' : 'top';
    typeGroups.forEach((g) => { g.hidden = g.dataset.qbTypes !== kind; });
    sel.type = 'Regular';
    const group = typeGroup();
    check($$('.acct-tile--type', group), (t) => t.dataset.type === sel.type);
    $('[data-qb-type-label]', group).textContent = `${group.dataset.typeWord}: ${sel.type}`;

    view.href = product.url;
    atbLabel.textContent = atbText();
  }

  $$('[data-qb-open]').forEach((btn) => btn.addEventListener('click', (e) => {
    e.preventDefault();
    if (drawer.isOpen) return;
    busy = false;
    fill(btn.closest('.acct-card'));
    drawer.open(btn);
    requestAnimationFrame(updateProgress);
    if (!state.reduced) {
      play([...gallery.children], { opacity: [0, 1], x: [24, 0] },
        { duration: MO.slow, ease: MO.eEntrance, delay: stagger(MO.stagTight, { startDelay: 0.12 }) });
    }
  }));
  return drawer;
}

/* ============================================================
   4 · SORT — Favorites' Sort Dropdown (2299:195116). The menu drops
   on --mo-base; a choice relabels the toggle and the grid re-flows
   (FLIP, entrance curve). The price sorts read each card's own price;
   the rest keep the saved (Recommended) order — the prototype has no
   dates or ratings to sort by.
   ============================================================ */
function initSort() {
  const root = $('[data-sort-root]');
  const grid = $('[data-sort-grid]');
  if (!root || !grid) return null;
  const toggle = $('[data-sort-toggle]', root);
  const menu = $('[data-sort-menu]', root);
  const chevron = $('[data-sort-chevron]', root);
  const label = $('[data-sort-label]', root);
  const options = $$('.acct-sort__option', root);
  const cards = [...grid.children];
  cards.forEach((c, i) => { c.dataset.order = i; });
  const priceOf = (c) => Number($('.acct-card__price', c).textContent.replace(/[^0-9.]/g, ''));
  let open = false;

  function setOpen(on, { focus = false } = {}) {
    if (on === open) return;
    open = on;
    toggle.setAttribute('aria-expanded', String(on));
    play(chevron, { rotate: on ? 180 : 0 }, { duration: MO.base, ease: MO.eEntrance });
    if (on) {
      menu.hidden = false;
      play(menu, { opacity: [0, 1], y: [-4, 0] }, { duration: MO.base, ease: MO.eEntrance });
      if (focus) (options.find((o) => o.getAttribute('aria-selected') === 'true') || options[0]).focus();
    } else {
      play(menu, { opacity: [1, 0] }, { duration: MO.quick, ease: MO.eExit })?.finished.then(() => { if (!open) menu.hidden = true; });
    }
  }

  function sortBy(key) {
    const order = [...cards];
    if (key === 'Price: Low to High') order.sort((a, b) => priceOf(a) - priceOf(b) || a.dataset.order - b.dataset.order);
    else if (key === 'Price: High to Low') order.sort((a, b) => priceOf(b) - priceOf(a) || a.dataset.order - b.dataset.order);
    else order.sort((a, b) => a.dataset.order - b.dataset.order);
    const first = new Map(cards.map((c) => [c, c.getBoundingClientRect()]));
    order.forEach((c) => grid.append(c));
    cards.forEach((c) => {
      const a = first.get(c);
      const b = c.getBoundingClientRect();
      const dx = a.left - b.left;
      const dy = a.top - b.top;
      if (dx || dy) play(c, { x: [dx, 0], y: [dy, 0] }, { duration: MO.slow, ease: MO.eEntrance });
    });
  }

  toggle.addEventListener('click', () => setOpen(!open, { focus: false }));
  toggle.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true, { focus: true }); }
  });
  options.forEach((opt, i) => {
    opt.addEventListener('click', () => {
      options.forEach((o) => o.setAttribute('aria-selected', String(o === opt)));
      swapText(label, `Sort: ${opt.dataset.sort}`);
      setOpen(false);
      toggle.focus({ preventScroll: true });
      sortBy(opt.dataset.sort);
      announce(`Sorted by ${opt.dataset.sort}`);
    });
    opt.addEventListener('keydown', (e) => {
      const n = { ArrowDown: 1, ArrowUp: -1 }[e.key];
      if (n) { e.preventDefault(); options[(i + n + options.length) % options.length].focus(); }
    });
  });
  document.addEventListener('click', (e) => { if (open && !root.contains(e.target)) setOpen(false); });
  document.addEventListener('keydown', (e) => {
    if (open && e.key === 'Escape') { setOpen(false); toggle.focus({ preventScroll: true }); }
  });
  return { close: () => setOpen(false) };
}

/* ============================================================
   5 · EDIT DRAWERS — Edit Account Details, Edit Password, Add / Edit
   Address, Shopping Preference. Each is the Figma frame as markup; on
   open the rows assemble behind the arriving panel (the PDP size grid's
   tight stagger), SAVE writes what was typed back to the page and the
   drawer closes. Labels float over a value (Underlined Input Form
   Filled / Focused); Show reveals a password.
   ============================================================ */
const drawers = {};

function openDrawer(key, trigger, prepare) {
  const d = drawers[key];
  if (!d) return;
  prepare?.(d.root);
  syncSelects(d.root);
  d.open(trigger);
  if (!state.reduced) {
    play($$('[data-drawer-item]', d.root), { opacity: [0, 1], y: [MO.riseXs, 0] },
      { duration: MO.base, ease: MO.eEntrance, delay: stagger(MO.stagTight, { startDelay: 0.16 }) });
  }
}

const syncSelects = (scope) => $$('.acct-field--select', scope).forEach((f) => {
  f.classList.toggle('is-filled', Boolean($('select', f).value));
});

function initDrawers() {
  $$('.acct-drawer[data-drawer]:not([data-drawer="quickbuy"])').forEach((root) => {
    drawers[root.dataset.drawer] = createDrawer(root, { side: true });
    $$('select', root).forEach((s) => s.addEventListener('change', () => syncSelects(root)));
  });
  $$('[data-pw-toggle]').forEach((btn) => btn.addEventListener('click', (e) => {
    e.preventDefault();
    const input = $('input', btn.closest('.acct-field'));
    const show = input.type === 'password';
    input.type = show ? 'text' : 'password';
    btn.textContent = show ? 'Hide' : 'Show';
  }));

  /* write a value back to the page with the label crossfade */
  const setText = (el, text) => { if (el && el.textContent !== text) swapText(el, text); };
  const submit = (key, fn) => {
    const form = $(`[data-form="${key}"]`);
    form?.addEventListener('submit', (e) => {
      e.preventDefault();
      const msg = fn(new FormData(form), form);
      drawers[key].close();
      if (msg) announce(msg);
    });
  };

  // Account Details → Edit Account Details (1975:156235)
  $$('[data-open="edit-account"]').forEach((b) => b.addEventListener('click', () => openDrawer('edit-account', b)));
  submit('edit-account', (data) => {
    setText($('[data-field="first"]'), data.get('first').trim() || 'Clara');
    setText($('[data-field="last"]'), data.get('last').trim() || 'Barton');
    if (data.get('occupation')) setText($('[data-field="occupation"]'), data.get('occupation'));
    const m = Number(data.get('month'));
    const d = Number(data.get('day'));
    if (m >= 1 && m <= 12 && d >= 1 && d <= 31) {
      const month = new Date(2000, m - 1, 1).toLocaleString('en-US', { month: 'long' });
      setText($('[data-field="birthday"]'), `${month} ${String(d).padStart(2, '0')}`);
    }
    return 'Account details saved.';
  });

  // Account Details → Edit Password (1975:163912)
  $$('[data-open="edit-password"]').forEach((b) => b.addEventListener('click', () => openDrawer('edit-password', b, (root) => {
    $$('input', root).forEach((i) => { i.value = ''; i.type = 'password'; });
    $$('[data-pw-toggle]', root).forEach((t) => { t.textContent = 'Show'; });
  })));
  submit('edit-password', () => 'Password updated.');

  // Addresses → Add Address (1977:165298) / Edit Address (2305:196562)
  const list = $('[data-addresses]');
  let editing = null;
  const linesOf = (a) => {
    const street = [a.street, a.apt].filter(Boolean).join(', ');
    return [`${street},`, `${a.city}${a.state ? `, ${a.state}` : ''} ${a.zip}`.trim(), a.phone].filter((l) => l && l !== ',').join('<br>');
  };
  const readEntry = (el) => Object.fromEntries(['first', 'last', 'street', 'apt', 'city', 'state', 'zip', 'phone'].map((k) => [k, el.dataset[k] || '']));
  const writeEntry = (el, a, isDefault) => {
    Object.entries(a).forEach(([k, v]) => { el.dataset[k] = v; });
    setText($('[data-address-name]', el), `${a.first} ${a.last}`.trim());
    const lines = $('[data-address-lines]', el);
    play(lines, { opacity: [0, 1] }, { duration: MO.base, ease: MO.eEntrance });
    lines.innerHTML = linesOf(a);
    if (isDefault) makeDefault(el);
  };
  const makeDefault = (el) => {
    const tag = $('[data-address-default]', list);
    if (tag && !el.contains(tag)) $('.acct-entry__label', el).append(tag);
    $$('[data-address]', list).forEach((x) => x.toggleAttribute('data-default', x === el));
  };
  const formValues = (data) => Object.fromEntries(['first', 'last', 'street', 'apt', 'city', 'state', 'zip', 'phone']
    .map((k) => [k, String(data.get(k) || '').trim()]));

  list?.addEventListener('click', (e) => {
    const b = e.target.closest('[data-open="edit-address"]');
    if (!b) return;
    editing = b.closest('[data-address]');
    openDrawer('edit-address', b, (root) => {
      const a = readEntry(editing);
      Object.entries(a).forEach(([k, v]) => { const f = $(`[name="${k}"]`, root); if (f) f.value = v; });
      $('[name="default"]', root).checked = editing.hasAttribute('data-default');
    });
  });
  submit('edit-address', (data) => {
    if (!editing) return '';
    writeEntry(editing, formValues(data), data.get('default') === 'default');
    return 'Address saved.';
  });
  $$('[data-open="add-address"]').forEach((b) => b.addEventListener('click', () => openDrawer('add-address', b, (root) => {
    $(`[data-form="add-address"]`).reset();
  })));
  submit('add-address', (data) => {
    const a = formValues(data);
    if (!a.street) return '';
    const el = $('[data-address]:not([data-default])', list)?.cloneNode(true) || $('[data-address]', list).cloneNode(true);
    $('[data-address-default]', el)?.remove();
    el.removeAttribute('data-default');
    list.append(el);
    writeEntry(el, a, data.get('default') === 'default');
    play(el, { opacity: [0, 1], y: [MO.riseSm, 0] }, { duration: MO.slow, ease: MO.eEntrance });
    return 'Address added.';
  });

  // Preferences → Shopping Preference (2177:246376)
  $$('[data-open="shopping"]').forEach((b) => b.addEventListener('click', () => openDrawer('shopping', b)));
  submit('shopping', (data) => {
    const shop = data.get('shop') || 'Women';
    const el = $('[data-pref-shop]');
    if (el && !el.textContent.startsWith(shop)) {
      play(el, { opacity: [0, 1], y: [4, 0] }, { duration: MO.base, ease: MO.eEntrance });
      el.innerHTML = `<span class="acct-pref__value-lead">${shop} — </span>${shop}’s collections, fits, and colors up front`;
    }
    return `Shopping preference set to ${shop}.`;
  });

  // Preferences → Size Preferences (2160:241309) · Embroidery Preferences (2160:241310)
  initSizePrefs(submit);
  initEmbroideryPrefs(submit);
}

/* ============================================================
   5b · SIZE + EMBROIDERY PREFERENCES — the two drawers behind the
   Preferences page's Sizes and Embroidery EDIT links. The data is the
   frames' own preset (the picks the page shows), but every control is
   live, so a reviewer can walk the experience as if changing it:
   Women / Men, the five categories, every size and type; Text / Logo,
   the two counted lines, font, position, the saved logos. SAVE writes
   the summary back to the page for the visit; closing without it
   leaves the page as it was, and the drawer reopens on what's saved.

   Motion: a category, gender or Text / Logo change swaps the body the
   way the account drawer swaps steps — the old rows fade on --mo-quick,
   the body's height moves to the new rows' on the standard curve
   (--mo-base) so SAVE below glides rather than jumps, and the new rows
   rise in on the tight stagger. Reduced: an instant swap.
   ============================================================ */

/* swap a drawer body's content, animating its own height between the two */
async function swapBody(stage, render) {
  if (state.reduced) { render(); return; }
  const rows = [...stage.children].filter((n) => !n.hidden);
  await play(rows, { opacity: [1, 0] }, { duration: MO.quick, ease: MO.eStandard })?.finished;
  const from = stage.getBoundingClientRect().height;
  render();
  const to = stage.getBoundingClientRect().height;
  /* pin the old height in the same task: motion's first height frame lands
     a frame late, and without the pin that frame paints the new height */
  stage.style.overflow = 'clip';
  stage.style.height = `${from}px`;
  play(stage, { height: [from, to] }, { duration: MO.base, ease: MO.eStandard })
    ?.finished.then(() => requestAnimationFrame(() => { stage.style.height = ''; stage.style.overflow = ''; }));
  const next = [...stage.children].filter((n) => !n.hidden);
  const items = next.flatMap((n) => (n.matches('.acct-prefs__view') ? [...n.children] : [n]));
  next.forEach((n) => { n.style.opacity = ''; });
  play(items, { opacity: [0, 1], y: [MO.riseXs, 0] },
    { duration: MO.base, ease: MO.eEntrance, delay: stagger(MO.stagTight, { startDelay: 0.04 }) });
}

/* a radiogroup of tiles: one on, arrow keys rove, a second tap on the
   chosen tile clears it when `clearable` */
function bindChoice(group, onPick, { clearable = false } = {}) {
  bindRoving(group, (tile) => {
    if (tile.getAttribute('aria-disabled') === 'true') {
      shake(tile);
      return;
    }
    const off = clearable && tile.getAttribute('aria-checked') === 'true';
    $$('[role="radio"]', group).forEach((t) => {
      const on = !off && t === tile;
      t.setAttribute('aria-checked', String(on));
      t.tabIndex = on ? 0 : -1;
    });
    if (off) tile.tabIndex = 0;
    onPick(off ? null : tile.dataset.value, tile);
  }, { selectOnArrow: false });
}
const setChoice = (group, value) => {
  const tiles = $$('[role="radio"]', group);
  tiles.forEach((t) => { const on = t.dataset.value === value; t.setAttribute('aria-checked', String(on)); t.tabIndex = on ? 0 : -1; });
  if (!tiles.some((t) => t.tabIndex === 0) && tiles[0]) tiles[0].tabIndex = 0;
};

/* a row of role=tab buttons: click or arrow keys move the selection */
function bindTabs(row, onSelect) {
  const tabs = $$('[role="tab"]', row);
  const select = (tab, focus) => {
    tabs.forEach((t) => { const on = t === tab; t.setAttribute('aria-selected', String(on)); t.tabIndex = on ? 0 : -1; });
    if (focus) tab.focus();
    onSelect(tab);
  };
  row.addEventListener('click', (e) => {
    const tab = e.target.closest('[role="tab"]');
    if (tab && tab.getAttribute('aria-selected') !== 'true') select(tab, false);
  });
  row.addEventListener('keydown', (e) => {
    const i = tabs.indexOf(document.activeElement);
    const step = { ArrowRight: 1, ArrowLeft: -1 }[e.key];
    if (i < 0 || !step) return;
    e.preventDefault();
    select(tabs[(i + step + tabs.length) % tabs.length], true);
  });
  return (value, key) => tabs.forEach((t) => { const on = t.dataset[key] === value; t.setAttribute('aria-selected', String(on)); t.tabIndex = on ? 0 : -1; });
}

/* --- Size Preferences --------------------------------------------------
   The women's runs and type / length rows are the frames' (Tops XXS–6XL +
   Size Type, Pants XXS–2XL + Length, Outerwear and Underscrubs XXS–2XL,
   Shoes 5–14.5). Figma draws no Men frames, so Men uses the lab's men's
   runs (the Core PLP: XS–6XL, Short / Regular / Tall) on the same layout. */
const APPAREL_11 = ['XXS', 'XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL', '6XL'];
const APPAREL_7 = APPAREL_11.slice(0, 7);
const MENS = APPAREL_11.slice(1);
const halves = (from, to) => Array.from({ length: (to - from) * 2 + 1 }, (_, i) => String(from + i / 2));
const SIZE_PREFS = {
  women: {
    tops: { sizes: APPAREL_11, variant: { heading: 'Select Size Type', word: 'Size Type', options: ['Regular', 'Petite'] } },
    pants: { sizes: APPAREL_7, variant: { heading: 'Select Length', word: 'Length', options: ['Regular', 'Petite', 'Tall'] } },
    outerwear: { sizes: APPAREL_7 },
    underscrubs: { sizes: APPAREL_7 },
    shoes: { sizes: halves(5, 14.5) },
  },
  men: {
    tops: { sizes: MENS },
    pants: { sizes: MENS, variant: { heading: 'Select Length', word: 'Length', options: ['Short', 'Regular', 'Tall'] } },
    outerwear: { sizes: MENS },
    underscrubs: { sizes: MENS },
    shoes: { sizes: [...halves(7, 13), '14', '15'] },
  },
};
const sizeName = (s) => SIZE_NAMES[s] || s;

function initSizePrefs(submit) {
  const root = $('[data-drawer="sizes"]');
  if (!root) return;
  const panel = $('[data-size-panel]', root);
  const tabsRow = $('[data-size-tabs]', root);
  const genders = $('[data-size-genders]', root);
  $$('[role="tab"]', tabsRow).forEach((t) => { t.dataset.label = t.textContent.trim(); });
  /* what the page shows (Figma 1980:169933) — kept with the signed-in session
     (figs-account.js), so a change here is what every PDP and Quick Buy
     preselects for this shopper */
  let saved = getFigsSizePrefs();
  let draft = structuredClone(saved);
  let cat = 'tops';

  const tileHTML = (cls, value, on, label = value) =>
    `<button class="acct-tile ${cls}" type="button" role="radio" aria-checked="${on}" tabindex="-1" data-value="${value}"><span class="acct-tile__text">${label}</span></button>`;
  function render() {
    const def = SIZE_PREFS[draft.gender][cat];
    const pick = draft[draft.gender][cat];
    let html = `<div class="acct-drawer__group">
        <p class="acct-drawer__group-label" id="sizes-size-label">Select Size</p>
        <div class="acct-prefs__tiles" role="radiogroup" aria-labelledby="sizes-size-label" data-size-pick="size">
          ${def.sizes.map((sz) => tileHTML('acct-tile--size', sz, pick.size === sz)).join('')}
        </div>
      </div>`;
    if (def.variant) {
      html += `<div class="acct-drawer__group">
        <p class="acct-drawer__group-label" id="sizes-variant-label">${def.variant.heading}</p>
        <div class="acct-prefs__tiles" role="radiogroup" aria-labelledby="sizes-variant-label" data-size-pick="variant">
          ${def.variant.options.map((o) => tileHTML('acct-tile--type', o, pick.variant === o)).join('')}
        </div>
      </div>`;
    }
    panel.innerHTML = html;
    panel.setAttribute('aria-labelledby', `sizes-tab-${cat}`);
    $$('[data-size-pick]', panel).forEach((group) => {
      setChoice(group, pick[group.dataset.sizePick] || '');
      bindChoice(group, (value) => { draft[draft.gender][cat][group.dataset.sizePick] = value || undefined; }, { clearable: true });
    });
  }

  const syncTabs = bindTabs(tabsRow, (tab) => { cat = tab.dataset.sizeCat; swapBody(panel, render); });
  /* Women / Men: never "none", and re-tapping the current one does nothing */
  $$('[role="radio"]', genders).forEach((c) => { c.dataset.value = c.dataset.sizeGender; });
  bindChoice(genders, (value) => {
    if (!value || value === draft.gender) return;
    draft.gender = value;
    swapBody(panel, render);
  });

  $$('[data-open="sizes"]').forEach((b) => b.addEventListener('click', () => openDrawer('sizes', b, () => {
    draft = structuredClone(saved);
    cat = 'tops';
    syncTabs('tops', 'sizeCat');
    setChoice(genders, draft.gender);
    $('[name="autofilter"]', root).checked = draft.autofilter;
    render();
  })));

  /* the page's cards follow what's saved — on arrival (a change made on an
     earlier visit) and as the drawer saves */
  function writeCards({ animate = true } = {}) {
    const g = saved.gender;
    Object.entries(SIZE_PREFS[g]).forEach(([key, def]) => {
      const card = $(`[data-pref-size="${key}"]`);
      if (!card) return;
      const pick = saved[g][key];
      const lines = pick.size
        ? `<p>Size: ${sizeName(pick.size)}</p>${def.variant && pick.variant ? `<p>${def.variant.word}: ${pick.variant}</p>` : ''}`
        : '<p>No size selected</p>';
      const box = $('[data-pref-lines]', card);
      if (box.innerHTML === lines) return;
      card.classList.toggle('acct-pref__card--empty', !pick.size);
      box.innerHTML = lines;
      if (animate) play(box, { opacity: [0, 1], y: [4, 0] }, { duration: MO.base, ease: MO.eEntrance });
    });
  }
  writeCards({ animate: false });

  submit('sizes', (data) => {
    draft.autofilter = data.get('autofilter') === 'on';
    saved = structuredClone(draft);
    setFigsSizePrefs(saved);
    writeCards();
    return `Size preferences saved for ${saved.gender === 'men' ? 'Men' : 'Women'}.`;
  });
}

/* --- Embroidery Preferences ---------------------------------------------
   Text: the frames' two lines (24 characters, counted), seven fonts and four
   positions. Logo: the saved logos — Figma draws wireframe squares, so they
   stay squares; the third is still being digitized and can't be chosen. */
function initEmbroideryPrefs(submit) {
  const root = $('[data-drawer="embroidery"]');
  if (!root) return;
  const stage = $('[data-emb-stage]', root);
  const views = { text: $('[data-emb-panel="text"]', root), logo: $('[data-emb-panel="logo"]', root) };
  const groups = Object.fromEntries($$('[data-emb-choice]', root).map((g) => [g.dataset.embChoice, g]));
  const logoName = $('[data-emb-logo-name]', root);
  let saved = { line1: 'Joan Davis, NP-C', line2: 'Registered Nurse', font: 'Sabin', position: 'Left Chest', logo: 'Modern Animal' };
  let draft = { ...saved };

  const count = (input) => {
    $(`[data-emb-count="${input.name}"]`, root).textContent = `${String(input.value.length).padStart(2, '0')}/${input.maxLength}`;
  };
  $$('[data-emb-count-for]', root).forEach((input) => input.addEventListener('input', () => count(input)));

  bindChoice(groups.font, (v) => { draft.font = v; });
  bindChoice(groups.position, (v) => { draft.position = v; });
  bindChoice(groups.logo, (v) => {
    draft.logo = v;
    swapText(logoName, v || 'None');
  });
  const syncModes = bindTabs($('[data-emb-modes]', root), (tab) => {
    const mode = tab.dataset.embMode;
    swapBody(stage, () => { views.text.hidden = mode !== 'text'; views.logo.hidden = mode !== 'logo'; });
  });

  $$('[data-open="embroidery"]').forEach((b) => b.addEventListener('click', () => openDrawer('embroidery', b, () => {
    draft = { ...saved };
    syncModes('text', 'embMode');
    views.text.hidden = false;
    views.logo.hidden = true;
    $$('[data-emb-count-for]', root).forEach((input) => { input.value = draft[input.name]; count(input); });
    setChoice(groups.font, draft.font);
    setChoice(groups.position, draft.position);
    setChoice(groups.logo, draft.logo);
    logoName.textContent = draft.logo || 'None';
  })));

  submit('embroidery', (data) => {
    saved = { ...draft, line1: String(data.get('line1') || '').trim(), line2: String(data.get('line2') || '').trim() };
    const write = (key, html) => {
      const box = $(`[data-pref-emb="${key}"] [data-pref-lines]`);
      if (!box || box.innerHTML === html) return;
      box.innerHTML = html;
      play(box, { opacity: [0, 1], y: [4, 0] }, { duration: MO.base, ease: MO.eEntrance });
    };
    const up = (t) => t.toUpperCase();
    write('text', [
      saved.line1 && `<p>First Line: ${up(saved.line1)}</p>`,
      saved.line2 && `<p>Second Line: ${up(saved.line2)}</p>`,
      saved.font && `<p>Font: ${saved.font}</p>`,
      saved.position && `<p>Position: ${saved.position}</p>`,
    ].filter(Boolean).join('') || '<p>No text saved</p>');
    write('logo', `<p>Logo: ${saved.logo || 'None'}</p><p>Position: Left Chest</p>`);
    return 'Embroidery preferences saved.';
  });
}

/* ============================================================
   6 · FIRST SCREEN + REVEALS — the templates' shared vocabulary.
   Intro: the account nav and the page heading rise 16px on
   --mo-slow, 40ms apart in reading order. Everything else reveals as
   it enters the viewport, batched per frame and sorted top-then-left
   (mo.js revealOnView's batching), so what's already on screen at
   load follows the heading in one sequence:
     data-reveal          copy rises 16 on --mo-slow
     data-reveal="card"   a product card rises 32 on --mo-deliberate,
                          its plate settles from 1.04 on --mo-cinematic
     data-reveal="media"  an order plate fades on --mo-deliberate and
                          settles from 1.04
   A [data-reveal-group] (a carousel row) reveals all its members when
   the row arrives, so cards still off-screen in a mobile rail are
   already there when they're swiped in.
   ============================================================ */
let bootAt = 0;
const INTRO = 0.12;

function initIntro() {
  tween($$('[data-intro]').sort(byPosition), { y: [MO.riseSm, 0], opacity: [0, 1] },
    { duration: MO.slow, ease: MO.eEntrance, delay: stagger(MO.stagTight, { startDelay: INTRO }) });
}

function settle(el, delay) {
  const imgs = $$('[data-settle]', el);
  if (imgs.length) tween(imgs, { scale: [MO.scaleIn, 1] }, { duration: MO.cinematic, ease: MO.eEntrance, delay });
}

function revealOne(el, delay) {
  const kind = el.dataset.reveal;
  if (kind === 'card') {
    tween(el, { y: [MO.riseMd, 0], opacity: [0, 1] }, { duration: MO.deliberate, ease: MO.eEntrance, delay });
    settle(el, delay);
  } else if (kind === 'media') {
    tween(el, { opacity: [0, 1] }, { duration: MO.deliberate, ease: MO.eEntrance, delay });
    settle(el, delay);
  } else {
    tween(el, { y: [MO.riseSm, 0], opacity: [0, 1] }, { duration: MO.slow, ease: MO.eEntrance, delay });
  }
}

function initReveals() {
  let queue = [];
  let raf = 0;
  const flush = () => {
    raf = 0;
    const batch = queue.sort(byPosition);
    queue = [];
    /* a batch that flushes during the load sequence queues behind the
       heading; anything revealed later, on scroll, goes straight away */
    const start = performance.now() - bootAt < 500 ? INTRO + 0.2 : 0;
    batch.forEach((el, i) => revealOne(el, start + i * MO.stagTight));
  };
  const groups = new Map();
  $$('[data-reveal]').forEach((el) => {
    const g = el.closest('[data-reveal-group]') || el;
    if (!groups.has(g)) groups.set(g, []);
    groups.get(g).push(el);
  });
  groups.forEach((els, g) => teardown.push(inView(g, () => {
    queue.push(...els);
    if (!raf) raf = requestAnimationFrame(flush);
  }, { margin: '0px 0px -10% 0px' })));
}

/* Plates push in on hover — the Homepage collection-tile recipe (1.04 on
   --mo-slow, entrance). Desktop pointers only; nothing under Reduced. */
function initPushIn() {
  const pushIn = (host, img) => teardown.push(hover(host, () => {
    if (state.reduced || !img) return undefined;
    track(animate(img, { scale: MO.scaleIn }, { duration: MO.slow, ease: MO.eEntrance }));
    return () => track(animate(img, { scale: 1 }, { duration: MO.slow, ease: MO.eEntrance }));
  }));
  $$('.acct-card__media').forEach((m) => pushIn(m, $('.acct-card__img', m)));
  $$('.acct-order__thumb').forEach((t) => pushIn(t, $('img', t)));
  $$('.acct-item__media').forEach((t) => pushIn(t, $('img', t)));
}

/* Homepage data-hover-link lift on the text links; press on buttons
   (no spring — those are reserved for add-to-bag and favourite). */
function initMicro() {
  $$('.acct-page .acct-link').forEach((l) => {
    teardown.push(hover(l, () => {
      track(animate(l, { y: -2, opacity: 0.82 }, { duration: MO.quick, ease: MO.eEntrance }));
      return () => track(animate(l, { y: 0, opacity: 1 }, { duration: MO.quick, ease: MO.eEntrance }));
    }));
  });
  $$('.acct-btn').forEach((el) => {
    teardown.push(press(el, () => {
      track(animate(el, { scale: MO.scalePress }, { duration: MO.instant, ease: MO.eStandard }));
      return () => track(animate(el, { scale: 1 }, { duration: MO.base, ease: MO.eEntrance }));
    }));
  });
}

/* ============================================================
   LAB BAR — the PDP's motion.dev toolbar. Speed scales every tracked
   control; Replay / Reduced tear down and rebuild boot(). Ships
   hidden; H toggles it.
   ============================================================ */
let quickBuy = null;
let sort = null;

function initLabBar() {
  const bar = $('[data-lab-bar]');
  const handle = $('[data-lab-handle]');
  const slider = $('#lab-speed');
  const readout = $('[data-readout]');
  slider.addEventListener('input', () => {
    const v = parseFloat(slider.value);
    readout.textContent = `${v.toFixed(2)}x`;
    setSpeed(v);
  });
  const rebuild = () => {
    [quickBuy, ...Object.values(drawers)].forEach((d) => d?.close());
    sort?.close();
    window.scrollTo({ top: 0, behavior: 'instant' });
    teardown.splice(0).forEach((fn) => { try { fn?.(); } catch (e) { /* ignore */ } });
    clearTracked();
    /* back to the pre-hidden state, so every reveal replays from zero. The
       reset goes through motion too, so its record of each element moves. */
    const pre = $$('[data-intro], [data-reveal]');
    pre.forEach((el) => { el.style.opacity = '0'; });
    animate(pre, { opacity: 0 }, { duration: 0 });
    animate($$('[data-settle]'), { scale: MO.scaleIn }, { duration: 0 });
    requestAnimationFrame(boot);
  };
  $('[data-replay]').addEventListener('click', rebuild);
  const rb = $('[data-reduced-btn]');
  rb.setAttribute('aria-pressed', String(state.reduced));
  rb.addEventListener('click', () => {
    state.reduced = !state.reduced;
    rb.setAttribute('aria-pressed', String(state.reduced));
    rebuild();
  });
  const gb = $('[data-grid-btn]');
  const ov = $('[data-grid-overlay]');
  gb.addEventListener('click', () => {
    const on = gb.getAttribute('aria-pressed') !== 'true';
    gb.setAttribute('aria-pressed', String(on));
    ov.dataset.on = String(on);
  });
  let killed = false;
  $('[data-hide-btn]').addEventListener('click', () => {
    killed = true;
    handle.hidden = true;
    bar.remove();
  });
  addEventListener('keydown', (e) => {
    if (killed) return;
    if (e.key.toLowerCase() !== 'h' || e.metaKey || e.ctrlKey || e.altKey) return;
    if (/^(input|textarea|select)$/i.test(e.target.tagName) || e.target.isContentEditable) return;
    const hide = !bar.hidden;
    bar.hidden = hide;
    handle.hidden = !hide;
    if (!hide) tween(bar, { y: [MO.riseMd, 0], opacity: [0, 1] }, { duration: MO.slow, ease: MO.eEntrance });
  });
  handle.addEventListener('click', () => {
    if (killed) return;
    bar.hidden = false;
    handle.hidden = true;
  });
}

/* ============================================================
   BOOT — one-time bindings first, then the replayable motion layer
   ============================================================ */
function initOnce() {
  initTabs();
  initPlaceholders();
  initCards();
  quickBuy = initQuickBuy();
  sort = initSort();
  initDrawers();
}

function boot() {
  document.documentElement.dataset.reduced = String(state.reduced);
  bootAt = performance.now();
  initIntro();
  initReveals();
  initPushIn();
  initMicro();
}

initOnce();
boot();
/* tells the head safety net the reveals are running, so .mo-pre stays put
   for the blocks that haven't scrolled into view yet */
document.documentElement.setAttribute('data-mo-ready', '');
initLabBar();

addEventListener('load', () => {
  const broken = $$('img').filter((i) => i.getAttribute('src') && i.complete && i.naturalWidth === 0 && i.loading !== 'lazy');
  if (broken.length) console.error(`[assets] ${broken.length} image(s) failed to load`, broken.map((i) => i.src));
  else console.info(`[assets] ${$$('img').length} images OK`);
});
console.info(`%cFIGS Motion Lab · 17 Account · ${page}`, 'font-weight:700',
  '\nMotion: motion.dev v12 (vanilla)', '\nReduced:', state.reduced);
